import React, { useState } from 'react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import Icon from './AppIcon';

const WebinarManagement = () => {
  const { currentColors } = useAdminTheme();
  const [webinars, setWebinars] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [newWebinar, setNewWebinar] = useState({
    title: '',
    description: '',
    speaker_name: '',
    speaker_role: '',
    speaker_bio: '',
    speaker_avatar: '',
    scheduled_date: '',
    duration_minutes: 60,
    webinar_type: 'recorded',
    subscription_required: 'basic',
    max_attendees: 100,
    meeting_url: '',
    recording_url: '',
    presentation_url: '',
    image_url: '',
    status: 'scheduled',
    tags: [],
    agenda: [],
    resources: []
  });

  const [editingWebinar, setEditingWebinar] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const saveWebinar = () => {
    if (!newWebinar.title.trim() || !newWebinar.speaker_name.trim()) {
      alert('Please fill in at least title and speaker name');
      return;
    }

    const webinarData = {
      ...newWebinar,
      id: `webinar-${Date.now()}`,
      scheduled_date: newWebinar.scheduled_date || new Date().toISOString(),
      registered_count: 0,
      attended_count: 0
    };

    setWebinars(prev => [webinarData, ...prev]);
    setNewWebinar({
      title: '',
      description: '',
      speaker_name: '',
      speaker_role: '',
      speaker_bio: '',
      speaker_avatar: '',
      scheduled_date: '',
      duration_minutes: 60,
      webinar_type: 'recorded',
      subscription_required: 'basic',
      max_attendees: 100,
      meeting_url: '',
      recording_url: '',
      presentation_url: '',
      image_url: '',
      status: 'scheduled',
      tags: [],
      agenda: [],
      resources: []
    });
    setShowCreateModal(false);
    alert('Webinar created successfully!');
  };

  const deleteWebinar = (webinarId) => {
    if (window.confirm('Are you sure you want to delete this webinar?')) {
      setWebinars(prev => prev.filter(webinar => webinar.id !== webinarId));
      alert('Webinar deleted successfully!');
    }
  };

  const editWebinar = (webinar) => {
    setEditingWebinar({
      ...webinar,
      scheduled_date: webinar.scheduled_date ? webinar.scheduled_date.split('T')[0] : ''
    });
    setShowEditModal(true);
  };

  const updateWebinar = () => {
    if (!editingWebinar.title.trim() || !editingWebinar.speaker_name.trim()) {
      alert('Please fill in at least title and speaker name');
      return;
    }

    setWebinars(prev =>
      prev.map(webinar =>
        webinar.id === editingWebinar.id
          ? { ...editingWebinar, scheduled_date: editingWebinar.scheduled_date + 'T10:00:00.000Z' }
          : webinar
      )
    );
    setEditingWebinar(null);
    setShowEditModal(false);
    alert('Webinar updated successfully!');
  };

  const updateWebinarStatus = (webinarId, newStatus) => {
    setWebinars(prev =>
      prev.map(webinar =>
        webinar.id === webinarId
          ? { ...webinar, status: newStatus }
          : webinar
      )
    );
    alert(`Webinar ${newStatus} successfully!`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return currentColors.info;
      case 'live': return currentColors.success;
      case 'completed': return currentColors.primary;
      case 'cancelled': return currentColors.error;
      default: return currentColors.textMuted;
    }
  };

  const webinarTypes = ['live', 'recorded', 'hybrid'];
  const subscriptionLevels = ['free', 'basic', 'premium', 'enterprise'];
  const statuses = ['scheduled', 'live', 'completed', 'cancelled'];


  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: currentColors.text }}>
            Webinar Library Management
          </h2>
          <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
            Schedule and manage live webinars and recorded sessions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90"
          style={{ backgroundColor: currentColors.primary, color: '#ffffff' }}
        >
          <Icon name="Plus" size={16} className="inline mr-2" />
          Schedule Webinar
        </button>
      </div>

      {/* Webinars List */}
      <div 
        className="rounded-xl shadow-sm border overflow-hidden"
        style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: currentColors.background }}>
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Webinar
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Speaker
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Scheduled Date
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Duration
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Video Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {webinars.map((webinar) => (
                <tr key={webinar.id} className="border-t" style={{ borderColor: currentColors.border }}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium" style={{ color: currentColors.text }}>
                        {webinar.title}
                      </p>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>
                        {webinar.description?.substring(0, 80)}...
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${currentColors.info}20`,
                            color: currentColors.info
                          }}
                        >
                          {webinar.registered_count || 0} registered
                        </span>
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${currentColors.warning}20`,
                            color: currentColors.warning
                          }}
                        >
                          {webinar.webinar_type}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium" style={{ color: currentColors.text }}>
                        {webinar.speaker_name}
                      </p>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>
                        {webinar.speaker_role}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4" style={{ color: currentColors.textSecondary }}>
                    {new Date(webinar.scheduled_date).toLocaleDateString()}
                    <br />
                    <span className="text-sm">
                      {new Date(webinar.scheduled_date).toLocaleTimeString()}
                    </span>
                  </td>
                  <td className="px-6 py-4" style={{ color: currentColors.textSecondary }}>
                    {webinar.duration_minutes} min
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-1">
                      <span
                        className="px-2 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${getStatusColor(webinar.status)}20`,
                          color: getStatusColor(webinar.status)
                        }}
                      >
                        {webinar.status}
                      </span>
                      {webinar.recording_url && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Video Available
                        </span>
                      )}
                      {!webinar.recording_url && webinar.status === 'completed' && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Video Pending
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedWebinar(webinar);
                          setShowDetailModal(true);
                        }}
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="View Details"
                      >
                        <Icon name="Eye" size={16} style={{ color: currentColors.info }} />
                      </button>
                      
                      <button 
                        onClick={() => editWebinar(webinar)}
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="Edit Webinar"
                      >
                        <Icon name="Edit" size={16} style={{ color: currentColors.primary }} />
                      </button>

                      {webinar.status === 'scheduled' && (
                        <button
                          onClick={() => updateWebinarStatus(webinar.id, 'live')}
                          className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                          title="Start Webinar"
                        >
                          <Icon name="Video" size={16} style={{ color: currentColors.success }} />
                        </button>
                      )}

                      {webinar.status === 'live' && (
                        <button
                          onClick={() => updateWebinarStatus(webinar.id, 'completed')}
                          className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                          title="End Webinar"
                        >
                          <Icon name="Square" size={16} style={{ color: currentColors.error }} />
                        </button>
                      )}

                      <button
                        onClick={() => deleteWebinar(webinar.id)}
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="Delete"
                      >
                        <Icon name="Trash2" size={16} style={{ color: currentColors.error }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Webinar Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-card rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                {showEditModal ? 'Edit Webinar' : 'Schedule New Webinar'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                }}
                className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-muted-foreground"
              >
                <Icon name="X" size={20} style={{ color: currentColors.text }} />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Webinar Title *
                  </label>
                  <input
                    type="text"
                    value={showEditModal ? editingWebinar.title : newWebinar.title}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, title: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, title: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="Enter webinar title..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Description *
                  </label>
                  <textarea
                    value={showEditModal ? editingWebinar.description : newWebinar.description}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, description: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, description: e.target.value }));
                      }
                    }}
                    rows={3}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="Describe the webinar content..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Speaker Name *
                  </label>
                  <input
                    type="text"
                    value={showEditModal ? editingWebinar.speaker_name : newWebinar.speaker_name}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, speaker_name: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, speaker_name: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="Speaker full name..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Speaker Role *
                  </label>
                  <input
                    type="text"
                    value={showEditModal ? editingWebinar.speaker_role : newWebinar.speaker_role}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, speaker_role: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, speaker_role: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="Speaker job title/role..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Speaker Bio
                  </label>
                  <textarea
                    value={showEditModal ? editingWebinar.speaker_bio : newWebinar.speaker_bio}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, speaker_bio: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, speaker_bio: e.target.value }));
                      }
                    }}
                    rows={2}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="Brief speaker biography..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Speaker Avatar URL
                  </label>
                  <input
                    type="url"
                    value={showEditModal ? editingWebinar.speaker_avatar : newWebinar.speaker_avatar}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, speaker_avatar: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, speaker_avatar: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="https://example.com/avatar.jpg"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Scheduled Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={showEditModal ? editingWebinar.scheduled_date : newWebinar.scheduled_date}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, scheduled_date: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, scheduled_date: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="480"
                    value={showEditModal ? editingWebinar.duration_minutes : newWebinar.duration_minutes}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Webinar Type *
                  </label>
                  <select
                    value={showEditModal ? editingWebinar.webinar_type : newWebinar.webinar_type}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, webinar_type: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, webinar_type: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  >
                    {webinarTypes.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Subscription Required *
                  </label>
                  <select
                    value={showEditModal ? editingWebinar.subscription_required : newWebinar.subscription_required}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, subscription_required: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, subscription_required: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  >
                    {subscriptionLevels.map(level => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Max Attendees
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={showEditModal ? editingWebinar.max_attendees : newWebinar.max_attendees}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, max_attendees: parseInt(e.target.value) }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, max_attendees: parseInt(e.target.value) }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Meeting URL (for live webinars)
                  </label>
                  <input
                    type="url"
                    value={showEditModal ? editingWebinar.meeting_url : newWebinar.meeting_url}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, meeting_url: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, meeting_url: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Recording URL (YouTube/Vimeo)
                  </label>
                  <input
                    type="url"
                    value={showEditModal ? editingWebinar.recording_url : newWebinar.recording_url}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, recording_url: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, recording_url: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="https://youtube.com/embed/... or https://vimeo.com/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Presentation/Slides URL
                  </label>
                  <input
                    type="url"
                    value={showEditModal ? editingWebinar.presentation_url : newWebinar.presentation_url}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, presentation_url: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, presentation_url: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="https://drive.google.com/file/... or https://slideshare.net/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Thumbnail Image URL
                  </label>
                  <input
                    type="url"
                    value={showEditModal ? editingWebinar.image_url : newWebinar.image_url}
                    onChange={(e) => {
                      if (showEditModal) {
                        setEditingWebinar(prev => ({ ...prev, image_url: e.target.value }));
                      } else {
                        setNewWebinar(prev => ({ ...prev, image_url: e.target.value }));
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="https://example.com/thumbnail.jpg"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                }}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{ backgroundColor: currentColors.border, color: currentColors.text }}
              >
                Cancel
              </button>
              <button
                onClick={showEditModal ? updateWebinar : saveWebinar}
                disabled={
                  !(showEditModal ? editingWebinar.title : newWebinar.title) ||
                  !(showEditModal ? editingWebinar.description : newWebinar.description) ||
                  !(showEditModal ? editingWebinar.speaker_name : newWebinar.speaker_name)
                }
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50"
                style={{ backgroundColor: currentColors.primary, color: '#ffffff' }}
              >
                {showEditModal ? 'Update Webinar' : 'Schedule Webinar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Webinar Detail Modal */}
      {showDetailModal && selectedWebinar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-card rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                Webinar Details
              </h3>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-muted-foreground"
              >
                <Icon name="X" size={20} style={{ color: currentColors.text }} />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-2xl font-bold mb-2" style={{ color: currentColors.text }}>
                  {selectedWebinar.title}
                </h4>
                <p className="text-lg mb-4" style={{ color: currentColors.textSecondary }}>
                  {selectedWebinar.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}>
                    {selectedWebinar.webinar_type}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: `${getStatusColor(selectedWebinar.status)}20`, color: getStatusColor(selectedWebinar.status) }}>
                    {selectedWebinar.status}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: `${currentColors.info}20`, color: currentColors.info }}>
                    {selectedWebinar.duration_minutes} minutes
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                      {selectedWebinar.registered_count || 0}
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      Registered
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                      {selectedWebinar.attended_count || 0}
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      Attended
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                      {selectedWebinar.max_attendees}
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      Max Capacity
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                      {selectedWebinar.duration_minutes}
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      Minutes
                    </p>
                  </div>
                </div>
              </div>

              {/* Video Player Section */}
              {selectedWebinar.recording_url && selectedWebinar.status === 'completed' && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-4" style={{ color: currentColors.text }}>
                    Webinar Recording
                  </h4>
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    {selectedWebinar.recording_url.includes('youtube.com') || selectedWebinar.recording_url.includes('youtu.be') ? (
                      <iframe
                        src={selectedWebinar.recording_url.replace('watch?v=', 'embed/')}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Webinar Recording"
                      />
                    ) : selectedWebinar.recording_url.includes('vimeo.com') ? (
                      <iframe
                        src={selectedWebinar.recording_url.replace('vimeo.com', 'player.vimeo.com/video')}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title="Webinar Recording"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <a
                          href={selectedWebinar.recording_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white hover:text-text-tertiary transition-colors"
                        >
                          <Icon name="Play" size={48} />
                          <p className="mt-2 text-sm">Click to watch recording</p>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div
                  className="p-4 rounded-lg border"
                  style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}
                >
                  <h5 className="font-semibold mb-2" style={{ color: currentColors.text }}>
                    Speaker Information
                  </h5>
                  <p style={{ color: currentColors.textSecondary }}>
                    <strong>Name:</strong> {selectedWebinar.speaker_name}
                  </p>
                  <p style={{ color: currentColors.textSecondary }}>
                    <strong>Role:</strong> {selectedWebinar.speaker_role}
                  </p>
                  {selectedWebinar.speaker_bio && (
                    <p style={{ color: currentColors.textSecondary }}>
                      <strong>Bio:</strong> {selectedWebinar.speaker_bio}
                    </p>
                  )}
                </div>

                <div 
                  className="p-4 rounded-lg border"
                  style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}
                >
                  <h5 className="font-semibold mb-2" style={{ color: currentColors.text }}>
                    Schedule & Access
                  </h5>
                  <p style={{ color: currentColors.textSecondary }}>
                    <strong>Date:</strong> {new Date(selectedWebinar.scheduled_date).toLocaleDateString()}
                  </p>
                  <p style={{ color: currentColors.textSecondary }}>
                    <strong>Time:</strong> {new Date(selectedWebinar.scheduled_date).toLocaleTimeString()}
                  </p>
                  <p style={{ color: currentColors.textSecondary }}>
                    <strong>Subscription:</strong> {selectedWebinar.subscription_required}
                  </p>
                  {selectedWebinar.meeting_url && (
                    <p style={{ color: currentColors.textSecondary }}>
                      <strong>Meeting URL:</strong> 
                      <a 
                        href={selectedWebinar.meeting_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-1 text-blue-500 hover:underline"
                      >
                        Join Meeting
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WebinarManagement;
